import React, { ReactNode } from 'react'
import { TraceSpanCategory } from '../domain/Trace'
import Milliseconds from '../helper/milliseconds'
import Percent from '../helper/percent'
import Router from '../router'
import Shorten from '../helper/shorten'

export interface SummaryLine {
    name: string
    callCount: number
    wallTime: number
    wallTimePercentage: number
    exclusiveWallTime: number
    exclusiveWallTimePercentage: number
    category: TraceSpanCategory
    id: string
    hasDetails: boolean
    fn?: string | null
}

interface Props {
    categories: { [category: string]: TraceSpanCategory }
    lines: SummaryLine[]
    selectedLineId: string | null
    params?: { [name: string]: string }
    defaultSort?: 'callCount' | 'wallTime' | 'exclusiveWallTime'
    router: Router
    route: string
    publicVersion: boolean
    details?: ReactNode
    onLineClick?: (line: SummaryLine) => void
    isAggregated: boolean
}

interface State {
    sort: 'callCount' | 'wallTime' | 'exclusiveWallTime'
    tbodyToTop: number
}

class Summary extends React.Component<Props> {
    public static defaultProps = {
        route: 'XhprofProfilerBundle.Profile.summary',
        publicVersion: false,
    }
    public state: State
    private tbodyRef

    constructor(props) {
        super(props)
        this.state = { sort: props.defaultSort || 'exclusiveWallTime', tbodyToTop: 200 }
        this.tbodyRef = React.createRef()
    }

    componentDidMount() {
        this.setState({ tbodyToTop: window.innerHeight - this.tbodyRef.getBoundingClientRect().top - 30 }) // 30 is the height of the footer
    }

    public setSortBy(sort: State['sort']) {
        this.setState({ sort })
    }

    public render() {
        const lines = this.sortSummaryLines(this.props.lines)

        return (
            <div className="row">
                <div className="col-lg-8 col-md-12">
                    <div className="box">
                        <div className="box-body">
                            <table className="table table-striped table-bordered table-scrollable summary">
                                <thead>
                                    <tr>
                                        <th>Category</th>
                                        <th>Summary</th>
                                        <th>
                                            Count{' '}
                                            <a
                                                className="pull-right"
                                                onClick={() => {
                                                    this.setSortBy('callCount')
                                                }}
                                            >
                                                <span className={this.state.sort === 'callCount' ? 'fa fa-sort-desc' : 'fa fa-sort'} />
                                            </a>
                                        </th>
                                        <th colSpan={2}>
                                            Total Duration{' '}
                                            <a
                                                className="pull-right"
                                                onClick={() => {
                                                    this.setSortBy('wallTime')
                                                }}
                                            >
                                                <span className={this.state.sort === 'wallTime' ? 'fa fa-sort-desc' : 'fa fa-sort'} />
                                            </a>
                                        </th>
                                        <th colSpan={2}>
                                            Self Duration{' '}
                                            <a
                                                className="pull-right"
                                                onClick={() => {
                                                    this.setSortBy('exclusiveWallTime')
                                                }}
                                            >
                                                <span
                                                    className={this.state.sort === 'exclusiveWallTime' ? 'fa fa-sort-desc' : 'fa fa-sort'}
                                                />
                                            </a>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody ref={element => (this.tbodyRef = element)} style={{ maxHeight: `${this.state.tbodyToTop}px` }}>
                                    {lines.map(line => {
                                        const summary = line.name ? line.name : 'No Summary'
                                        return (
                                            <tr key={line.id} className={line.id == this.props.selectedLineId ? 'selected' : undefined}>
                                                <td>
                                                    <div className={'category category-' + line.category.className}>
                                                        {line.category.label}
                                                    </div>
                                                </td>
                                                <td>
                                                    {line.hasDetails ? (
                                                        <a
                                                            onClick={() => {
                                                                this.props.onLineClick?.(line)
                                                            }}
                                                        >
                                                            <Shorten string={summary} />
                                                        </a>
                                                    ) : (
                                                        <Shorten string={summary} />
                                                    )}
                                                </td>
                                                <td className="number">
                                                    {this.props.isAggregated
                                                        ? line.callCount % 1 === 0
                                                            ? line.callCount
                                                            : line.callCount.toFixed(2)
                                                        : line.callCount}
                                                </td>
                                                <td className="number">
                                                    <Milliseconds value={line.wallTime} />
                                                </td>
                                                <td className="number">
                                                    <Percent value={line.wallTimePercentage} />
                                                </td>
                                                <td className="number">
                                                    {line.exclusiveWallTime === 0 ? '-' : <Milliseconds value={line.exclusiveWallTime} />}
                                                </td>
                                                <td className="number">
                                                    {line.exclusiveWallTimePercentage === 0 ? (
                                                        '-'
                                                    ) : (
                                                        <Percent value={line.exclusiveWallTimePercentage} />
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4 col-md-12">{this.props.details}</div>
            </div>
        )
    }

    private sortSummaryLines(lines: SummaryLine[]) {
        return lines.sort((a, b) => {
            const key = this.state.sort

            // sort by name, if values are equal
            if (a[key] === b[key]) {
                return a.name.localeCompare(b.name)
            }

            return b[key] - a[key]
        })
    }
}

export default Summary
